/* eslint-disable max-lines, max-lines-per-function, complexity, no-restricted-syntax -- TODO: Refactor Chat to reduce file size and complexity */
import {
  ACCEPTED_FILE_TYPES,
  CAPPED_NOTICE_MIN_MESSAGE_LIMIT,
  CHARACTER_MODERATION_STATUS,
  CHARACTER_VISIBILITY,
  CharacterFlow,
  CharacterType,
  ChatRole,
  CloneConversationType,
  compareErrorCode,
  ConversationFlow,
  ICharacter,
  IConversationMessage,
  IPersona,
  isAgeVerificationMandatory,
  NotificationType,
  RateLimitingErrorType,
  setIsOverlayLogin,
  TypingMode,
  useApp,
  useAppDispatch,
  useAppSelector,
  useCloneConversation,
  useConversationMediaFiles,
  useLoadCharacter,
  useMessageFlagging,
  useOpenViewMediaModal,
  usePersona,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import {
  CapabilityGate,
  CapabilityKey,
  CapabilityUnavailableBehavior,
  SubCapabilityKey,
  useApplicationCapabilities,
  useApplicationCapabilitiesAllowed,
} from "@next-platform/client-common-core/infra/application/capabilities";
import {
  Alert,
  AlertActions,
  AlertVariant,
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Text,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { Divider, Image, Spacer, Spinner } from "@nextui-org/react";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AgeVerificationWarningModal } from "@/components";
import { CharacterTranslation } from "@/components/CharacterTranslation/CharacterTranslation";
import { CloneConversationModal } from "@/components/Chats";
import ErrorModal from "@/components/common/ErrorModal";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import { Col, Grid, Row, SpicyButton } from "@/components/customComponents";
import ScreenLoader from "@/components/customComponents/ScreenLoader";
import ChoosePersona from "@/components/Personas/ChoosePersona";
import Trans from "@/components/Trans/Trans";
import QueuePosition from "@/components/WaitingQueue/QueuePosition";
import WaitingQueueModal from "@/components/WaitingQueue/WaitingQueueModal";
import { CHAT_PAGE_WIDTH, CHOOSE_PERSONA_LOCAL_STORAGE_KEY, FEATURE_FLAGS, FREE_CHARACTER_ID, QueueStatus, ROUTES } from "@/constants";
import { MINIMUM_PARTICIPANT_LIMIT } from "@/constants/groupChat";
import { useAgeVerificationFlags, useFeatureFlag } from "@/hooks";
import { useChatJsonLdTokens } from "@/hooks/useChatJsonLdTokens";
import useToast from "@/hooks/useToast";
import AlertBubble from "@/pages/Chat/components/AlertBubble";
import CappedMessagesNotice from "@/pages/Chat/components/CappedMessagesNotice";
import CharacterRating from "@/pages/Chat/components/CharacterRating";
import ChatBar from "@/pages/Chat/components/ChatBubble/ChatBar";
import ChatBubble from "@/pages/Chat/components/ChatBubble/ChatBubble";
import { VoiceUpgradeVariant } from "@/pages/Chat/components/ChatBubble/types";
import TypingBubble from "@/pages/Chat/components/ChatBubble/TypingBubble";
import { isChatImageShown } from "@/pages/Chat/components/ChatBubble/utils";
import ChatDebugWidget from "@/pages/Chat/components/ChatDebugWidget/ChatDebugWidget";
import { ChatHeroContainer } from "@/pages/Chat/components/ChatHeroContainer";
import { ChatHeroCharacterDetailsProps } from "@/pages/Chat/components/ChatHeroContainer/types";
import ChatInput from "@/pages/Chat/components/ChatInput";
import ChatTopMenu from "@/pages/Chat/components/ChatTopMenu";
import FloatingUpgradeCta from "@/pages/Chat/components/ContextCapUpsell/FloatingUpgradeCta";
import FlagModal from "@/pages/Chat/components/FlagModal/FlagModal";
import GroupChatCharacterSelector from "@/pages/Chat/components/GroupChatCharacterSelector";
import ImageDropOverlay from "@/pages/Chat/components/ImageDropOverlay";
import RulebookHud from "@/pages/Chat/components/RulebookHud/RulebookHud";
import { AlertBubbleVariant, ChatPageModal } from "@/pages/Chat/components/types";
import UpgradeToPremiumModal from "@/pages/Chat/components/UpgradeToPremiumModal/UpgradeToPremiumModal";
import { ChatModalVoiceUpgrade } from "@/pages/Chat/components/voices/ChatModalVoiceUpgrade";
import { PREFERRED_VOICE_ACTIONS, PREMIUM_PLAN_ROUTE } from "@/pages/Chat/components/voices/constant";
import useGreetingAudioImpression from "@/pages/Chat/components/voices/useGreetingAudioImpression";
import VoiceRatingModal from "@/pages/Chat/components/voices/VoiceRatingModal";
import { IRequestErrorHandler } from "@/pages/Chat/hooks/types";
import { useChatSessionTracking } from "@/pages/Chat/hooks/useChatSessionTracking";
import useConversation from "@/pages/Chat/hooks/useConversation";
import useFileDragAndDrop from "@/pages/Chat/hooks/useFileDragAndDrop";
import usePreferredVoice from "@/pages/Chat/hooks/usePreferredVoice";
import chooseCharacterFromMessage from "@/pages/Chat/utils/chooseCharacterFromMessage";
import getChatImageBlurVerdicts from "@/pages/Chat/utils/getChatImageBlurVerdicts";
import { PERSONA_ERRORS } from "@/pages/Personas/contants";
import useAuth from "@/services/useAuth";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl, getIsPartner } from "@/utils";
import { typedLocalStorageService } from "@/utils/localStorageRe";

export const RESPONSE_MODE_AUTO = "AUTO";

const SECONDS_PER_MINUTE = 60;
const IMAGE_HEIGHT = 20;
const IMAGE_WIDTH = 20;

const chatPageMaxWidthStyle = { maxWidth: CHAT_PAGE_WIDTH };

// Module scope so the array reference stays stable — it feeds the character-load effect's deps.
const CHAT_CHARACTER_INCLUDES: "rulebook"[] = ["rulebook"];

export const Chat = () => {
  const { characterId, conversationId } = useParams();
  const navigate = useNavigate();
  // `isUserAuthenticated` is the flag each bubble blurs by; the page's own `isAuthenticated` below comes from
  // the auth provider and can disagree with it for a moment, which would make the modal disagree with the bubble.
  const { userProfile, onlyUserId, isAuthenticated: isUserAuthenticated } = useUserInfo();
  const location = useLocation();

  const {
    personas,
    selectedPersona,
    setSelectedPersonaIds,
    onUserConfirmChangePersonaInConversation,
    setPersonaToChat,
    loading,
    onUserConfirmSelectedPersonaToChat,
    getDefaultPersona,
    resetSelectedPersonaIds,
    resetSelectedPersonaToChat,
    defaultPersonaId,
    selectedPersonaIds,
    errorType: personaErrorType,
  } = usePersona();
  const { applicationName, isNsfwEnabled } = useApp();
  const { isAuthenticated } = useAuth();
  const { name, avatar_url: user_avatar_url } = selectedPersona || getDefaultPersona() || {};
  const voiceUpgradeVariant: VoiceUpgradeVariant = isAuthenticated ? VoiceUpgradeVariant.MEMBER : VoiceUpgradeVariant.GUEST;

  const { status: waitingQueueStatus, position, time_left } = useAppSelector(({ waitingQueue }) => waitingQueue) || {};
  const [isNewConversation, setIsNewConversation] = useState(false);
  const isDoNotShowChoosePersonaChecked = (typedLocalStorageService.getItem(CHOOSE_PERSONA_LOCAL_STORAGE_KEY) as unknown as string) === "true";
  const { characterData: character, errorType: characterLoadErrorType } = useLoadCharacter({
    characterId: characterId ?? "",
    withTranslation: true,
    include: CHAT_CHARACTER_INCLUDES,
  });
  const hasCharacterLoadFailed = !character && characterLoadErrorType === CharacterFlow.GET_CHARACTER;
  // Hardcoded this value, this one should be come from BE, this prop should describe is auto response activated or not
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [hasPendingMessage, setHasPendingMessage] = useState(false);
  const [isAgeVerificationVisible, setIsAgeVerificationVisible] = useState<boolean | undefined>(undefined);
  const {
    conversation: convo,
    currentConversationId,
    createConversation,
    isTyping: typingMode,
    generate,
    altMsgs: altMessages,
    altMsgPosition,
    changeAltMsgPosition,
    onNewConversation,
    errorCode,
    resetError,
    removeMessages,
    isRemoveMessageLoading,
    removeMessageIdx,
    setRemoveMessageIdx,
    removalMode,
    setRemovalMode,
    setConversation: setConvo,
    setAltMsgs: setAltMessages,
    loadMessages,
    lastMessageId,
    isLastMessageIdDeleted,
    cappedMessageId,
    loadMoreMessages,
    isLoadingMessages,
    sendRating,
    invalidUrlError,
    handleCloneTitleUpdate,
    isCloneUpdateTitleLoading,
    conversationLabel,
    isChoosePersona,
    setIsChoosePersona,
    messagesLoading,
    displayError,
    displaySuccess,
    handleLoadedMessages,
    voiceId,
    setVoiceId,
    errorMessage,
    errorType,
    errorTitle: conversationErrorTitle,
    onUserUpdateMessage,
    isLoading,
    loadingType,
    handleDeleteMessageImage,
    isImageDeleteFromMessageLoading,
  } = useConversation({
    characterId: characterId ?? "",
    character,
    conversationId,
    name: name ?? "",
    selectedPersona,
    personas,
    setPersonaToChat: setPersonaToChat as (persona: IPersona | null) => void,
    isDoNotShowChoosePersonaChecked,
    getDefaultPersona,
    defaultPersonaId,
    setSelectedPersonaIds,
    setIsNewConversation,
  });

  // Recombee view_portion tracking
  const { sendViewPortionEvent } = useChatSessionTracking({
    conversationId: currentConversationId,
    characterId: characterId ?? "",
    isPublicBot: character?.visibility === CHARACTER_VISIBILITY.PUBLIC,
  });

  const [selectedMessageToPlay, setSelectedMessageToPlay] = useState<string | null>(null);
  const {
    handlePreferredVoiceActions,
    rateVoiceQuality,
    currentAudioMessageId,
    isAudioLoading,
    rateVoiceModal,
    handleRateVoiceQualityModalClose,
    setInfoMessageForRatingVoice,
    infoMessage,
    loadingFromPreferredVoice,
    errorMessage: voiceErrorMessage,
    resetSocketErrors,
  } = usePreferredVoice({
    voiceId,
    setVoiceId,
    conversationId: currentConversationId || "",
    handleLoadedMessages,
    displaySuccess,
    displayError,
    setSelectedMessageToPlay,
  });
  const conversation = convo as IConversationMessage[]; // convo is actually just an array of messages
  const setConversation = setConvo as Dispatch<SetStateAction<IConversationMessage[]>>;
  const altMsgs = altMessages as IConversationMessage[];
  // A brand-new chat has no conversation yet: the HUD shows turn-0 meters without waiting for messages.
  const isRulebookHudConversationLoaded = !currentConversationId || (!isLoadingMessages && conversation.length > 0);
  const setAltMsgs = setAltMessages as Dispatch<SetStateAction<IConversationMessage[]>>;

  const dispatch = useAppDispatch();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatInputWrapperRef = useRef<HTMLDivElement>(null);
  const [showWaitingQueueModal, setShowWaitingQueueModal] = useState(true);
  const [showVoiceSelectionModal, setShowVoiceSelectionModal] = useState(false);
  const [voiceSelectionModalForMessage, setVoiceSelectionModalForMessage] = useState(false);
  const [showVoiceUpgradeModal, setShowVoiceUpgradeModal] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showModelSettings, setShowModelSettings] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [isUpdateCloneTitleModal, setIsUpdateCloneTitleModal] = useState(false);
  const [isInputActionDisabled, setIsInputActionDisabled] = useState(false);
  const [isGroupBelowMinimum, setIsGroupBelowMinimum] = useState(false);
  const [isShowConversationMemoryModal, setIsShowConversationMemoryModal] = useState(false);
  const [isShowChangePersonaConfirmationModal, isSetShowChangePersonaConfirmationModal] = useState(false);
  const [showChatbotRatingModal, setShowChatbotRatingModal] = useState(false);
  const [showAutoResponseUpgradeModal, setShowAutoResponseUpgradeModal] = useState(false);

  const [restrictScrollToBottom, setRestrictScrollToBottom] = useState(false);
  const savedScrollTop = useRef(0);
  const savedScrollHeight = useRef(0);
  const savedConversationLength = useRef(0);
  const prevConversationLengthRef = useRef(0);
  const justRestoredScroll = useRef(false);
  const displayErrorRef = useRef(displayError);
  const resetSocketErrorsRef = useRef(resetSocketErrors);

  // Keep refs updated with latest function references
  displayErrorRef.current = displayError;
  resetSocketErrorsRef.current = resetSocketErrors;

  const isServerErrorBubble = errorMessage;

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const { blockedUsers, blurNSFW, autoResponse, onUserSetAutoResponse } = useUserSettings();
  const { hasFeatureFlag: hasContextCapUpsell } = useFeatureFlag(FEATURE_FLAGS.CONTEXT_CAP_UPSELL);
  const { isAgeVerificationEnabled, isAgeVerificationRequired } = useAgeVerificationFlags();

  const autoResponseCapability = useApplicationCapabilities(SubCapabilityKey.GroupChatAutoResponse);
  // The chat hands the modal the images it shows of its loaded messages to step through, so an image the
  // NSFW gate withholds is not one step away. The alternatives of the latest reply are loaded too but held
  // apart from the conversation, so they count like they do for cloning.
  const nsfwChatCapability = useApplicationCapabilities(SubCapabilityKey.NsfwChat);
  const nsfwImageCapability = useApplicationCapabilities(SubCapabilityKey.NsfwImage);
  const shownImageMessages = useMemo(() => {
    const gate = {
      isNsfwEnabled,
      isNsfwChatAllowed: !!nsfwChatCapability?.allowed,
      isNsfwChatPaywalled: nsfwChatCapability?.unavailableBehavior === CapabilityUnavailableBehavior.SHOW_PAYWALL,
      isNsfwImageAllowed: !!nsfwImageCapability?.allowed,
      isAgeVerificationRequired,
    };
    return [...conversation, ...altMsgs].filter((message) => isChatImageShown(message, gate));
  }, [conversation, altMsgs, isNsfwEnabled, nsfwChatCapability, nsfwImageCapability, isAgeVerificationRequired]);
  const { fileIds: conversationMediaFileIds } = useConversationMediaFiles({ messages: shownImageMessages });
  // One verdict per file, resolved the way each bubble resolves its own, so the modal shows whichever file a
  // step lands on the way its bubble did, even when a group chat's images span characters.
  const conversationMediaBlurVerdicts = useMemo(
    () => getChatImageBlurVerdicts({ messages: shownImageMessages, character, blurNSFW, isAuthenticated: isUserAuthenticated }),
    [shownImageMessages, character, blurNSFW, isUserAuthenticated],
  );
  const openMediaFileDetails = useOpenViewMediaModal();
  // Opening the details modal from a chat image is its own capability, and the read behind it needs the
  // media capability; a brand without either keeps the plain zoom on chat images.
  const isOpenMediaDetailsAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ChatImageOpenMediaDetails);
  const isMediaAllowed = useApplicationCapabilitiesAllowed(CapabilityKey.Media);
  const handleOpenImageDetails = useCallback(
    (mediaFileId: string) => openMediaFileDetails(mediaFileId, conversationMediaFileIds, conversationMediaBlurVerdicts),
    [openMediaFileDetails, conversationMediaFileIds, conversationMediaBlurVerdicts],
  );
  const isGroupChatAutoResponseAllowed = autoResponseCapability?.allowed ?? false;
  const isGroupChatAutoResponsePaywall = autoResponseCapability?.isPaywall ?? false;

  const isGroupCharacter = character?.type === CharacterType.META;

  const { isDesktop, hoverNone, isMobile, isTablet } = useMedia();
  const isPartner = getIsPartner();
  const [isChangePersona, setIsChangePersona] = useState(false);
  const {
    onFlagMessageHandler,
    success: messageFlagSuccess,
    isLoading: isFlaggingMessage,
    error: messageFlagError,
    resetErrorAndSuccess,
    progressStep,
    setProgressStep,
  } = useMessageFlagging();
  const [isFlagModal, setIsFlagModal] = useState(false);
  const [messageToFlag, setMessageToFlag] = useState<{ id: string; isAltMsg?: boolean } | null>(null);
  const [characterThatIsTyping, setCharacterThatIsTyping] = useState<ICharacter | null>(null);

  // use clone conversation
  const {
    onUserCancelCloning,
    onUserStartCloning,
    onUserSelectedCloningType,
    isCloningConversationInProgress,
    onResetCloneForNextUse,
    clonedConversationInfo,
    cloningConversationError,
    isUserChoiceLastMessageRequired: isUserSelectsPartialCloning,
    onUserConfirmPartialCloning,
    shouldCurrentMessageBeSelectedForCloning,
    setLastMsgIndexForPartialCloning,
    selectedMessageIndex: selectedMessageIndexForPartialCloning,
  } = useCloneConversation();

  const { hasFeatureFlag: hasChatUserImageFlag, isLoading: isFeatureFlagLoading } = useFeatureFlag(FEATURE_FLAGS.CHAT_USER_IMAGE);
  const shouldRenderImageUploader = hasChatUserImageFlag && !isFeatureFlagLoading && !isGroupCharacter;
  const hasInitializedPartialCloneFromChatsPageRef = useRef(false);
  const isCloningFromSavedChatsRef = useRef(false);
  const savedChatsCloneParamsRef = useRef<{ cloneType: CloneConversationType; cloneTitle: string } | null>(null);

  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const {
    isDragOver,
    isValidFileType,
    dragHandlers = {},
  } = useFileDragAndDrop({
    onFilesDrop: (files: File[]) => {
      setDroppedFile(files[0]);
    },
    acceptedFileTypes: [...ACCEPTED_FILE_TYPES],
  });
  const imageDragAndDropHandlers = shouldRenderImageUploader ? dragHandlers : {};

  const { displayToast } = useToast();
  const { t } = useTranslation();
  // Chat disclaimer based on application name and device dimension
  const PC_DISCLAIMER = t("chat:page.disclaimer.desktop", { applicationName });
  const MOBILE_DISCLAIMER = t("chat:page.disclaimer.mobile");

  const [isCloneConversationModal, setIsCloneConversationModal] = useState(false);
  const [isMessageEditMode, setIsMessageEditMode] = useState(false);
  const enableEditableChatExperience = waitingQueueStatus !== QueueStatus.WAITING || isPartner || characterId === FREE_CHARACTER_ID;

  // Handle cloning from saved chats page
  const locationState = location.state as {
    cloneType?: CloneConversationType;
    cloneTitle?: string;
  } | null;
  const isCloningTriggeredFromChatsPage = locationState?.cloneType && locationState?.cloneTitle;

  const resetSavedChatsPartialClone = useCallback(() => {
    isCloningFromSavedChatsRef.current = false;
    savedChatsCloneParamsRef.current = null;
  }, []);

  const onUserStartCloningFromMenu = useCallback(
    (messages: IConversationMessage[]) => {
      resetSavedChatsPartialClone();
      onUserStartCloning(messages);
    },
    [onUserStartCloning, resetSavedChatsPartialClone],
  );

  const handleNewConversation = useCallback(() => {
    resetSavedChatsPartialClone();
    void onNewConversation();
  }, [onNewConversation, resetSavedChatsPartialClone]);

  // character endpoint returns empty object when someone tries to access a private bot not owned by them.
  const isBotAccessAllowed = character && Object.keys(character)?.length > 0 && !invalidUrlError;
  const isBotCreatorBlocked = blockedUsers?.includes(character?.creator_user_id ?? "");

  const isChatInputHidden = !enableEditableChatExperience || !isBotAccessAllowed || isBotCreatorBlocked;
  //This will resolve to true if currentChat is not a groupChat or has no enough members
  const isInvalidChat = character?.type === "META" && (character?.sub_characters?.length ?? 0) < MINIMUM_PARTICIPANT_LIMIT;

  const isCreatorOfFixRequiredBot =
    character?.moderationReport?.moderation_status === CHARACTER_MODERATION_STATUS.FIX_REQUIRED &&
    !!onlyUserId &&
    character?.creator_user_id === onlyUserId;

  useEffect(() => {
    if (character?.aicq_manual_review_required === true) {
      navigate(ROUTES.chatbots, {
        replace: true,
      });
      return;
    }
    if (isCreatorOfFixRequiredBot) {
      const editPath = character?.type === CharacterType.META ? `/group/edit/${characterId}` : `/chatbot/edit/${characterId}`;
      navigate(editPath, { replace: true });
      return;
    }
    setIsGroupBelowMinimum(isInvalidChat);
  }, [character?.aicq_manual_review_required, isCreatorOfFixRequiredBot, isInvalidChat, navigate, character?.type, characterId]);

  useEffect(() => {
    const errorMsg = (personaErrorType && PERSONA_ERRORS[personaErrorType]) || voiceErrorMessage;

    if (errorMsg) {
      displayErrorRef.current(errorMsg);
      resetSocketErrorsRef.current();
    }
  }, [personaErrorType, voiceErrorMessage]);

  // Without this, a character fetch that fails to load (e.g. the chat's stale on a
  // group whose membership just changed) leaves `character` permanently null, and the
  // `!character` spinner below never clears — this is what turns a failed load into an
  // infinite loading state instead of a visible failure.
  useEffect(() => {
    if (hasCharacterLoadFailed) {
      displayErrorRef.current();
      navigate(ROUTES.chatbots, { replace: true });
    }
  }, [hasCharacterLoadFailed, navigate]);

  const scrollToBottom = useCallback(() => {
    if (!chatContainerRef.current) return;
    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    if (isAgeVerificationEnabled && character?.is_nsfw && isAgeVerificationMandatory(userProfile?.age_verification?.status)) {
      setIsAgeVerificationVisible(true);
    }
  }, [isAgeVerificationEnabled, character?.is_nsfw, userProfile?.age_verification?.status]);

  useLayoutEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const currentLength = (conversation?.length || 0) + (altMessages?.length || 0);
    const prevLength = prevConversationLengthRef.current;
    prevConversationLengthRef.current = currentLength;

    if (restrictScrollToBottom) {
      // Wait for messages to actually load (conversation length must increase)
      if (currentLength <= savedConversationLength.current) {
        return;
      }

      // Restore scroll position after loading previous messages
      const heightAdded = chatContainer.scrollHeight - savedScrollHeight.current;
      chatContainer.scrollTop = savedScrollTop.current + heightAdded;

      justRestoredScroll.current = true;
      setRestrictScrollToBottom(false);
    } else {
      // Skip scroll-to-bottom if we just restored scroll
      if (justRestoredScroll.current) {
        justRestoredScroll.current = false;
        return;
      }
      if (!currentConversationId && !typingMode && !errorMessage) {
        return;
      }

      const didAppendMessage = currentLength > prevLength;
      if (didAppendMessage || typingMode || errorMessage) {
        scrollToBottom();
      }
    }
  }, [conversation, altMessages, restrictScrollToBottom, typingMode, currentConversationId, errorMessage, scrollToBottom]);

  useEffect(() => {
    return () => {
      if (isNewConversation) {
        setIsNewConversation(false);
      }
      resetSelectedPersonaIds();
      resetSelectedPersonaToChat();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Cleanup only on unmount
  }, []);

  useEffect(() => {
    if (messageFlagSuccess) {
      displaySuccess();
      const flaggedId = messageToFlag?.id;
      const filterFlagged = (msgs: IConversationMessage[]) => msgs.filter((msg) => msg.id !== flaggedId);
      if (messageToFlag?.isAltMsg) {
        setAltMsgs(filterFlagged);
        changeAltMsgPosition(-1);
      } else {
        setConversation(filterFlagged);
      }
    }
    if (messageFlagError) {
      displayError();
    }

    return () => resetErrorAndSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when flag operation completes, not when messageToFlag changes
  }, [messageFlagSuccess, messageFlagError]);

  useEffect(() => {
    const { character_id, id: clonedConversationId } = clonedConversationInfo || {};
    if (!clonedConversationId && !cloningConversationError) return;

    if (clonedConversationId) {
      displayToast({ message: t("chat:page.cloneConversation.toast.success"), type: NotificationType.success });
      // only redirect user to clone chat if clone conversation is not triggered from saved chats page. Because we redirect back to saved chats page on that case.
      const chatPath = `/chat/${character_id}/${clonedConversationId}`;
      if (!isCloningFromSavedChatsRef.current) navigate(chatPath);
    }
    if (cloningConversationError) {
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
    }
    onResetCloneForNextUse();
    setLastMsgIndexForPartialCloning(-1);

    // redirect back to saved chats page if clone conversation is triggered from there on both success and failure.
    if (isCloningFromSavedChatsRef.current) {
      resetSavedChatsPartialClone();
      navigate(-1);
    }
  }, [
    cloningConversationError,
    clonedConversationInfo,
    displayToast,
    t,
    navigate,
    onResetCloneForNextUse,
    setLastMsgIndexForPartialCloning,
    resetSavedChatsPartialClone,
  ]);

  useEffect(() => {
    if (hasInitializedPartialCloneFromChatsPageRef.current) {
      return;
    }

    if (isCloningTriggeredFromChatsPage && !savedChatsCloneParamsRef.current) {
      savedChatsCloneParamsRef.current = {
        cloneType: locationState!.cloneType as CloneConversationType,
        cloneTitle: locationState!.cloneTitle as string,
      };
      isCloningFromSavedChatsRef.current = true;
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }

    const cloneParams = savedChatsCloneParamsRef.current;
    if (!cloneParams) {
      return;
    }

    const totalMessageCount = (conversation?.length ?? 0) + (altMsgs?.length ?? 0);
    if (totalMessageCount === 0) {
      return;
    }

    onUserStartCloning([...conversation, ...altMsgs]);
    onUserSelectedCloningType(cloneParams.cloneType, cloneParams.cloneTitle);
    hasInitializedPartialCloneFromChatsPageRef.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps -- capture saved-chats navigation once, then start partial clone when messages load
  }, [isCloningTriggeredFromChatsPage, conversation?.length, altMsgs?.length, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!selectedMessageToPlay || showVoiceSelectionModal || !voiceId) {
      return;
    }
    void handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Play, messageId: selectedMessageToPlay });
    setSelectedMessageToPlay(null);
  }, [selectedMessageToPlay, showVoiceSelectionModal, handlePreferredVoiceActions, voiceId]);

  useEffect(() => {
    // Handle case where user opens voice selection during new conversation before bot response. Store greeting ID once available to play message instantly.
    if (!voiceSelectionModalForMessage || selectedMessageToPlay) {
      return;
    }
    const greetingMessageId = convo?.[0]?.id;
    if (!greetingMessageId) {
      return;
    }
    setSelectedMessageToPlay(greetingMessageId);
    setVoiceSelectionModalForMessage(false);
  }, [convo, voiceSelectionModalForMessage, selectedMessageToPlay]);

  const handleVoiceButtonClick = useCallback((messageId: string) => {
    setSelectedMessageToPlay(messageId);
    setVoiceSelectionModalForMessage(true);
  }, []);

  const handleRestrictScrollToBottom = useCallback(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    savedScrollTop.current = chatContainer.scrollTop;
    savedScrollHeight.current = chatContainer.scrollHeight;
    savedConversationLength.current = (conversation?.length || 0) + (altMsgs?.length || 0);
    setRestrictScrollToBottom(true);
  }, [conversation?.length, altMsgs?.length]);

  const ImageData = useMemo(() => {
    return {
      src: getAvatarUrl(character?.avatar_url ?? "", {
        preset: AvatarPreset.IMAGE_500X650,
      }),
      alt: character?.name,
    };
  }, [character]);

  const isCharacterAvatarBlurred = !!((blurNSFW && character?.avatar_is_nsfw) || (!isAuthenticated && character?.is_nsfw));

  const chatHeroData: ChatHeroCharacterDetailsProps = useMemo(() => {
    const heroData = {
      src: ImageData?.src,
      name: character?.name,
      tags: character?.tags,
      title: character?.title,
      blurred: isCharacterAvatarBlurred,
    };

    Object.assign(heroData, { sub_characters: character?.sub_characters });
    return heroData;
  }, [character, ImageData, isCharacterAvatarBlurred]);

  const disclaimerMessage = isMobile || isTablet ? MOBILE_DISCLAIMER : PC_DISCLAIMER;
  const disclaimer = disclaimerMessage;
  const chatHeroAlertOptions = useMemo(
    () => ({
      visible: !isAuthenticated,
      title: !isMobile ? t("chat:page.alert.unregistered.title.desktop") : t("chat:page.alert.unregistered.title.mobile"),
      description: !isMobile ? t("chat:page.alert.unregistered.description") : undefined,
    }),
    [isAuthenticated, isMobile, t],
  );

  // Group chat: When user selects a character or clicks on next button
  const handleCharacterClick = useCallback(
    async (characterId: string | null) => {
      const isNextButton = characterId === null;

      if (isNextButton && autoResponseCapability?.isPaywall) {
        setShowAutoResponseUpgradeModal(true);
        return;
      }
      if (isNextButton && !isGroupChatAutoResponseAllowed) {
        return;
      }

      const selectedCharacter = isNextButton ? character : character?.sub_characters?.find((char: ICharacter) => char.id === characterId);

      if (!selectedCharacter) return;

      setCharacterThatIsTyping(selectedCharacter);

      const result = await generate({
        type: "text",
        content: pendingUserMessage || "",
        subCharacterId: isNextButton ? null : characterId,
        resubmit: false,
        regenerate: false,
        autopilot: false,
        image_prompt: "",
        continue_chat: !pendingUserMessage,
      });

      const conversationIdFromResult = result && typeof result === "object" && "conversation_id" in result ? result.conversation_id : undefined;
      sendViewPortionEvent(conversationIdFromResult);
    },
    [
      autoResponseCapability,
      isGroupChatAutoResponseAllowed,
      character,
      pendingUserMessage,
      generate,
      setShowAutoResponseUpgradeModal,
      setCharacterThatIsTyping,
      sendViewPortionEvent,
    ],
  );

  const handleAutoResponseChange = useCallback(
    (bool: boolean) => {
      if (autoResponseCapability?.isPaywall) {
        setShowAutoResponseUpgradeModal(true);
        return;
      }
      if (!isGroupChatAutoResponseAllowed) {
        return;
      }

      onUserSetAutoResponse(bool);
      displayToast({
        message: bool ? t("chat:page.header.dropdown.autoResponse.toast.enabled") : t("chat:page.header.dropdown.autoResponse.toast.disabled"),
        type: NotificationType.default,
      });

      if (bool && pendingUserMessage) {
        handleCharacterClick(null);
      }
    },
    [autoResponseCapability, isGroupChatAutoResponseAllowed, onUserSetAutoResponse, displayToast, t, pendingUserMessage, handleCharacterClick],
  );

  const handleChangeAltMsgPosition = useCallback(
    (position: number) => {
      handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
      changeAltMsgPosition(position);
    },
    [handlePreferredVoiceActions, changeAltMsgPosition],
  );

  const onCancelClick = useCallback(() => {
    if (removalMode) {
      setRemovalMode(false);
      setRemoveMessageIdx(null);
    } else {
      onUserCancelCloning();
      if (isCloningFromSavedChatsRef.current) {
        resetSavedChatsPartialClone();
        navigate(-1);
      }
    }
  }, [removalMode, setRemovalMode, setRemoveMessageIdx, onUserCancelCloning, navigate, resetSavedChatsPartialClone]);

  const onCloneButtonClick = useCallback(() => {
    onUserConfirmPartialCloning();
  }, [onUserConfirmPartialCloning]);

  const handleCreateConversation = useCallback(
    async (args: Pick<IConversationMessage, "role" | "content">[]) => {
      await createConversation(args);
    },
    [createConversation],
  );

  const resetAllError = useCallback(() => {
    resetError();
  }, [resetError]);

  const clearPending = useCallback(() => {
    setPendingUserMessage(null);
    setHasPendingMessage(false);
  }, [setPendingUserMessage, setHasPendingMessage]);

  const cleanup = useCallback(() => {
    setRemovalMode(false);
    setRemoveMessageIdx(null);
  }, [setRemovalMode, setRemoveMessageIdx]);

  const handleFlagMessage = useCallback(
    (args: { comment: string; reason: string }) => {
      const { comment, reason } = args;
      const payload = {
        message_id: messageToFlag?.id ?? "",
        conversation_id: currentConversationId ?? "",
        character_id: characterId ?? "",
        label: reason,
        comment,
      };
      onFlagMessageHandler(payload);
    },
    [messageToFlag?.id, currentConversationId, characterId, onFlagMessageHandler],
  );

  const handleFlagModalOpen = useCallback(
    (id: string, isAltMsg: boolean) => {
      setMessageToFlag({ id, isAltMsg });
      setIsFlagModal(true);
    },
    [setMessageToFlag, setIsFlagModal],
  );

  const handleFlagModalClose = useCallback(() => {
    setMessageToFlag(null);
    setIsFlagModal(false);
  }, [setMessageToFlag, setIsFlagModal]);
  // Checking if the last message is an image or not
  const isLastMessageImage = !!(
    (altMsgs?.length && altMsgs[0]?.image_url) ||
    (conversation?.length && conversation[conversation?.length - 1]?.image_url)
  );

  const closeCloneConversationModal = useCallback(() => {
    setIsCloneConversationModal(false);
  }, [setIsCloneConversationModal]);

  const onUserConfirmCloneChoice = useCallback(
    (cloneType: CloneConversationType, cloneTitle: string) => {
      onUserSelectedCloningType(cloneType, cloneTitle);
      closeCloneConversationModal();
    },
    [onUserSelectedCloningType, closeCloneConversationModal],
  );

  const handleModalVisibility = useCallback(
    (modal: ChatPageModal, isModalVisible: boolean) => {
      switch (modal) {
        case ChatPageModal.SHOW_VOICE_SELECION_MODAL:
          setShowVoiceSelectionModal(isModalVisible);
          // if modal is close then only reset this value
          if (!isModalVisible) {
            setVoiceSelectionModalForMessage(false);
          }
          break;
        case ChatPageModal.SHOW_VOICE_UPGRADE_MODAL:
          setShowVoiceUpgradeModal(isModalVisible);
          break;
        case ChatPageModal.SHOW_SHARE:
          setShowShare(isModalVisible);
          break;
        case ChatPageModal.SHOW_MODEL_SETTINGS:
          setShowModelSettings(isModalVisible);
          break;
        case ChatPageModal.SHOW_BLOCK_MODAL:
          setShowBlockModal(isModalVisible);
          break;
        case ChatPageModal.UPDATE_CLONE_TITLE_MODAL:
          setIsUpdateCloneTitleModal(isModalVisible);
          break;
        case ChatPageModal.CONVERSATION_MEMORY_MODAL:
          setIsShowConversationMemoryModal(isModalVisible);
          break;
        case ChatPageModal.CHANGE_PERSONA_CONFIRMATION_MODAL:
          isSetShowChangePersonaConfirmationModal(isModalVisible);
          break;
        case ChatPageModal.CLONE_CONVERSATION_MODAL:
          setIsCloneConversationModal(isModalVisible);
          break;
        case ChatPageModal.CHATBOT_RATING_MODAL:
          setShowChatbotRatingModal(isModalVisible);
          break;
        case ChatPageModal.SHOW_AUTO_RESPONSE_UPGRADE_MODAL:
          setShowAutoResponseUpgradeModal(isModalVisible);
          break;
        default:
          break;
      }
    },
    [
      setShowVoiceSelectionModal,
      setVoiceSelectionModalForMessage,
      setShowVoiceUpgradeModal,
      setShowShare,
      setShowModelSettings,
      setShowBlockModal,
      setIsUpdateCloneTitleModal,
      setIsShowConversationMemoryModal,
      isSetShowChangePersonaConfirmationModal,
      setIsCloneConversationModal,
      setShowChatbotRatingModal,
      setShowAutoResponseUpgradeModal,
    ],
  );

  const isAnyModalVisible = useCallback(() => {
    return (
      removalMode ||
      errorModalVisible ||
      isFlagModal ||
      isChangePersona ||
      isNewConversation ||
      isCloneConversationModal ||
      isUpdateCloneTitleModal ||
      isShowConversationMemoryModal ||
      isShowChangePersonaConfirmationModal ||
      showVoiceSelectionModal ||
      showShare ||
      showModelSettings ||
      showBlockModal ||
      rateVoiceModal.isVisible ||
      showAutoResponseUpgradeModal
    );
  }, [
    removalMode,
    errorModalVisible,
    isFlagModal,
    isChangePersona,
    isNewConversation,
    isCloneConversationModal,
    isUpdateCloneTitleModal,
    isShowConversationMemoryModal,
    isShowChangePersonaConfirmationModal,
    showVoiceSelectionModal,
    showShare,
    showModelSettings,
    showBlockModal,
    rateVoiceModal.isVisible,
    showAutoResponseUpgradeModal,
  ]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isAnyModalVisible() || document.activeElement?.tagName === "TEXTAREA" || document.activeElement?.tagName === "INPUT") {
        return;
      }
      if (event.key === "ArrowLeft") {
        handleChangeAltMsgPosition(-1);
      } else if (event.key === "ArrowRight") {
        handleChangeAltMsgPosition(1);
      }
    },
    [isAnyModalVisible, handleChangeAltMsgPosition],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Clean up pending message state when it's been added to conversation
  useEffect(() => {
    if (pendingUserMessage && conversation?.length > 0) {
      const lastMessage = conversation[conversation.length - 1];
      if (lastMessage?.role === ChatRole.USER && lastMessage?.content === pendingUserMessage) {
        setPendingUserMessage(null);
        setHasPendingMessage(false);
      }
    }
  }, [conversation, pendingUserMessage]);

  const isRateLimitBreachedError = compareErrorCode(errorCode as string, RateLimitingErrorType.RATE_LIMIT_BREACHED);
  const isGuestUserWithRateLimitBreachedError = !isAuthenticated && isRateLimitBreachedError;
  const isLoggedInUserWithRateLimitBreachedError = isAuthenticated && isRateLimitBreachedError;

  const isGenericSendError = !!errorMessage && errorType === ConversationFlow.GENERATE_MESSAGE && !errorCode;

  // Roll back the optimistic (uncommitted) user message on a generic send error
  // so it is not shown as a bubble; the composer retains the text for retry.
  const refHandledSendError = useRef(false);
  useEffect(() => {
    if (isGenericSendError && !refHandledSendError.current) {
      refHandledSendError.current = true;
      setConversation((previousMessages) => {
        const lastMessage = previousMessages.at(-1);
        if (lastMessage?.role === ChatRole.USER && !lastMessage.id) {
          return previousMessages.slice(0, -1);
        }
        return previousMessages;
      });
    }
    if (!errorMessage) {
      refHandledSendError.current = false;
    }
  }, [isGenericSendError, errorMessage, setConversation]);

  const handleDismissSendError = useCallback(
    (action: AlertActions) => {
      if (action === AlertActions.Dismiss) {
        resetAllError();
      }
    },
    [resetAllError],
  );

  const handleErrorAction = useCallback(() => {
    if (isGuestUserWithRateLimitBreachedError) {
      dispatch(setIsOverlayLogin(true));
    } else {
      resetAllError();
      generate({ resubmit: true });
    }
  }, [isGuestUserWithRateLimitBreachedError, resetAllError, generate, dispatch]);

  const handlePendingMessage = useCallback(
    (message: string) => {
      resetAllError();
      setPendingUserMessage(message);
      setHasPendingMessage(true);
    },
    [resetAllError, setPendingUserMessage, setHasPendingMessage],
  );

  const getActionTitle = useCallback(() => {
    if (isGuestUserWithRateLimitBreachedError) {
      return t("chat:page.action.signUp");
    }
    if (!isLoggedInUserWithRateLimitBreachedError) {
      return t("chat:page.action.resubmit");
    }
  }, [isGuestUserWithRateLimitBreachedError, isLoggedInUserWithRateLimitBreachedError, t]);

  const requestErrorHandler: IRequestErrorHandler = useMemo(() => {
    return {
      isError: !!isServerErrorBubble,
      errorMessage: errorMessage,
      errorTitle: conversationErrorTitle || undefined,
      actionHandler: handleErrorAction,
      variant: isGuestUserWithRateLimitBreachedError ? AlertVariant.DEFAULT : AlertVariant.ERROR,
      actionTitle: getActionTitle(),
    };
  }, [isServerErrorBubble, errorMessage, conversationErrorTitle, handleErrorAction, isGuestUserWithRateLimitBreachedError, getActionTitle]);

  const messagesToRender = useMemo(() => {
    const allMessages = [...conversation];

    if (altMsgs.length > 0 && altMsgPosition >= 0 && altMsgPosition < altMsgs.length && !typingMode) {
      allMessages.push(altMsgs[altMsgPosition]);
    }
    if (!pendingUserMessage) return allMessages;

    const lastMessage = allMessages.at(-1);
    const alreadyInConversation = lastMessage?.role === ChatRole.USER && lastMessage?.content === pendingUserMessage;

    if (alreadyInConversation) return allMessages;

    const pendingMessage: IConversationMessage = {
      id: "pending-temp",
      role: ChatRole.USER,
      content: pendingUserMessage,
      created_at: new Date().toISOString(),
      conversation_id: currentConversationId || "",
      prev_id: "",
      createdAt: new Date().getTime(),
    };

    return [...allMessages, pendingMessage];
  }, [pendingUserMessage, conversation, altMsgs, altMsgPosition, typingMode, currentConversationId]);

  const greetingMessage = messagesToRender[0]?.role === ChatRole.BOT ? messagesToRender[0] : undefined;
  useGreetingAudioImpression({ character, greetingMessage, currentConversationId });

  const errorTargetBubbleIndex = errorMessage && messagesToRender.length > 0 ? messagesToRender.length - 1 : -1;

  const handleRemoveMesages = useCallback(() => {
    if (removeMessageIdx === null || removeMessageIdx === undefined) return;

    resetAllError();

    const messageToRemove = messagesToRender[removeMessageIdx];
    const lastMessage = messagesToRender[messagesToRender.length - 1];
    const lastIsPending = lastMessage?.id === "pending-temp";

    if (messageToRemove?.id === "pending-temp") {
      clearPending();
      cleanup();
      return;
    }

    removeMessages();

    if (lastIsPending) clearPending();
  }, [removeMessageIdx, messagesToRender, resetAllError, removeMessages, clearPending, cleanup]);

  const isChatPageScrollable = chatContainerRef.current && chatContainerRef.current?.scrollHeight > chatContainerRef.current?.clientHeight;
  useLayoutEffect(() => {
    if (!chatInputWrapperRef.current) {
      return;
    }
    const container = chatInputWrapperRef.current;
    if (isChatPageScrollable && !hoverNone && !isMobile) {
      container.style.marginLeft = "-10px";
    } else {
      container.style.marginLeft = "0px";
    }
  }, [isChatPageScrollable, hoverNone, isMobile]);

  const showGroupChat =
    character?.sub_characters &&
    !isChatInputHidden &&
    (!isInputActionDisabled || hasPendingMessage) &&
    !isGroupBelowMinimum &&
    !isUserSelectsPartialCloning &&
    !removalMode &&
    typingMode === null;

  const commonBubbleBarProps = {
    conversationLength: conversation?.length,
    currentConversationId,
    handlePreferredVoiceActions,
    isClonePartial: isUserSelectsPartialCloning,
    onRegenerate: generate,
    removalMode,
    setIsMessageEditMode,
    voiceId,
  };

  const uniqueBubbleProps = {
    // The details modal shows nothing to a logged out reader, so a guest keeps the plain zoom.
    onOpenImageDetails: isOpenMediaDetailsAllowed && isMediaAllowed && isUserAuthenticated ? handleOpenImageDetails : undefined,
    handleFlagModalOpen,
    infoMessage,
    isMessageEditMode,
    isRemoveMessageLoading,
    onCloneSelected: setLastMsgIndexForPartialCloning,
    onRemovalSelected: setRemoveMessageIdx,
    showCheckbox: removalMode || isUserSelectsPartialCloning,
    user_avatar_url,
    user_name: name ?? "",
    handleDeleteMessageImage,
    isImageDeleteFromMessageLoading,
  };

  const uniqueChatBarProps = {
    showVoiceSelectionModal,
    setCharacterThatIsTyping,
    allowRegenerate: false,
    character,
    sendRating,
    currentAudioMessageId,
    defaultPersonaId,
    isAudioLoading,
    loadMessages,
    handleModalVisibility,
    handleVoiceButtonClick,
    scrollToBottom,
    isTyping: typingMode,
  };

  const generateWithTracking = useCallback(
    async (opts?: Parameters<typeof generate>[0]) => {
      const isUserMessage = !opts?.resubmit && !opts?.regenerate && !opts?.autopilot && !opts?.continue_chat;
      const result = await generate(opts);
      if (isUserMessage) {
        // Pass conversation_id from result for first message (when currentConversationId is not yet set)
        const conversationIdFromResult = result && typeof result === "object" && "conversation_id" in result ? result.conversation_id : undefined;
        sendViewPortionEvent(conversationIdFromResult);
      }
      return result;
    },
    [generate, sendViewPortionEvent],
  );

  const handleOnPressVoiceUpgrade = useCallback(() => {
    if (isAuthenticated) {
      return navigate(PREMIUM_PLAN_ROUTE);
    }
    dispatch(setIsOverlayLogin(true));
  }, [isAuthenticated, navigate, dispatch]);

  const handleSetRemovalMode = useCallback(() => setRemovalMode(true), [setRemovalMode]);

  const handleAutoResponseUpdate = useCallback(() => handleAutoResponseChange(!autoResponse), [handleAutoResponseChange, autoResponse]);

  const handleTranslationSuccess = useCallback(() => {
    setSelectedPersonaIds([selectedPersona?.id as string]);
    setIsNewConversation(true);
    setIsChangePersona(true);
  }, [setSelectedPersonaIds, selectedPersona?.id, setIsNewConversation, setIsChangePersona]);

  const handleLoadPreviousMessages = useCallback(() => {
    handleRestrictScrollToBottom();
    loadMoreMessages();
  }, [handleRestrictScrollToBottom, loadMoreMessages]);

  const handleEditPendingMessage = useCallback((content: string) => setPendingUserMessage(content), [setPendingUserMessage]);

  const handleClickPrevious = useCallback(() => handleChangeAltMsgPosition(-1), [handleChangeAltMsgPosition]);
  const handleClickNext = useCallback(() => handleChangeAltMsgPosition(1), [handleChangeAltMsgPosition]);

  const handleShowWaitingQueueModal = useCallback(() => setShowWaitingQueueModal(true), [setShowWaitingQueueModal]);

  const handleCloseAutoResponseUpgradeModal = useCallback(() => setShowAutoResponseUpgradeModal(false), [setShowAutoResponseUpgradeModal]);

  const handleCloseChatbotRatingModal = useCallback(() => handleModalVisibility(ChatPageModal.CHATBOT_RATING_MODAL, false), [handleModalVisibility]);

  const handleCloseAgeVerification = useCallback(() => setIsAgeVerificationVisible(false), [setIsAgeVerificationVisible]);

  const isCutoffMessageLoaded = useMemo(() => {
    if (!cappedMessageId || conversation.length <= CAPPED_NOTICE_MIN_MESSAGE_LIMIT) {
      return false;
    }
    return conversation.some((msg) => msg.id === cappedMessageId);
  }, [cappedMessageId, conversation]);

  // Scroll to cutoff point
  const handleJumpToCutoff = useCallback(() => {
    if (!cappedMessageId) return;

    const element = document.getElementById(`message-${cappedMessageId}`);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth" as ScrollBehavior,
        block: "center",
      });
    }
  }, [cappedMessageId]);

  const metaProperties = useMemo(() => ({ characterName: character?.name ?? undefined }), [character?.name]);
  const jsonLdTokens = useChatJsonLdTokens(character?.name);

  const subCharactersWithFallback = character?.sub_characters ?? [];

  if (messagesLoading || !character || (isAuthenticated && !personas?.length)) {
    return <Spinner className="absolute top-1/2 left-1/2 scale-[1.5]" />;
  }

  if (isAuthenticated && (isChoosePersona || isChangePersona)) {
    return (
      <ChoosePersona
        personas={personas}
        selectedPersona={selectedPersona}
        character={character}
        setIsChoosePersona={setIsChoosePersona}
        setIsChangePersona={setIsChangePersona}
        isDoNotShow={isDoNotShowChoosePersonaChecked}
        setSelectedPersonaIds={setSelectedPersonaIds}
        onUserConfirmChangePersonaInConversation={onUserConfirmChangePersonaInConversation}
        conversationId={currentConversationId}
        loading={loading}
        isChangePersona={isChangePersona}
        onUserConfirmSelectedPersonaToChat={onUserConfirmSelectedPersonaToChat}
        onNewConversation={handleNewConversation}
        setIsNewConversation={setIsNewConversation}
        isNewConversation={isNewConversation}
        resetSelectedPersonaIds={resetSelectedPersonaIds}
        defaultPersonaId={defaultPersonaId}
        selectedPersonaIds={selectedPersonaIds}
        handlePreferredVoiceActions={handlePreferredVoiceActions}
      />
    );
  }

  return (
    <>
      <PageStaticMetaTags
        pageId="chat"
        properties={metaProperties}
        ogImage={
          getAvatarUrl(character?.avatar_url ?? "", {
            preset: AvatarPreset.IMAGE_500X650,
          }) ?? undefined
        }
        jsonLdTokens={jsonLdTokens}
      />
      <div className="flex grow flex-col top-0 left-0 w-full h-full bg-gray-2 relative" {...imageDragAndDropHandlers}>
        {!isBotAccessAllowed && (
          <Helmet>
            <meta name="prerender-status-code" content="404" />
          </Helmet>
        )}
        {/* chat header  */}
        <Row
          className={"w-full bg-gray-2 py-0 px-2 z-[2] items-center flex-col"} // Or any background color to prevent content from showing through
        >
          <Col className="max-w-[800px] w-full">
            {character.id === FREE_CHARACTER_ID && waitingQueueStatus === QueueStatus.WAITING && <QueuePosition />}
            <ChatTopMenu
              onNewConversation={handleNewConversation}
              onClearConversationErrors={resetAllError}
              character={character}
              onRemoveChat={handleSetRemovalMode}
              isShowRemove={(conversation?.length ?? 0) > 1 || (altMsgs?.length ?? 0) > 0}
              conversationLength={conversation?.length}
              setErrorModalVisible={setErrorModalVisible}
              setErrorModalMessage={setErrorModalMessage}
              isBotAccessAllowed={isBotAccessAllowed ?? false}
              conversation={conversation}
              altMsgs={altMsgs}
              conversationId={currentConversationId ?? ""}
              handleCloneTitleUpdate={handleCloneTitleUpdate}
              handleAutoResponseUpdate={handleAutoResponseUpdate}
              isAutoResponseActivated={autoResponse}
              conversationLabel={conversationLabel}
              setIsChangePersona={setIsChangePersona}
              setIsNewConversation={setIsNewConversation}
              selectedPersonaId={selectedPersona?.id}
              setSelectedPersonaIds={setSelectedPersonaIds}
              isDoNotShowChoosePersonaChecked={isDoNotShowChoosePersonaChecked}
              onUserStartCloning={onUserStartCloningFromMenu}
              voiceId={voiceId}
              handlePreferredVoiceActions={handlePreferredVoiceActions}
              isShowChangePersonaConfirmationModal={isShowChangePersonaConfirmationModal}
              isShowConversationMemoryModal={isShowConversationMemoryModal}
              isUpdateCloneTitleModal={isUpdateCloneTitleModal}
              showBlockModal={showBlockModal}
              showModelSettings={showModelSettings}
              showShare={showShare}
              showVoiceSelectionModal={showVoiceSelectionModal}
              showChatbotRatingModal={showChatbotRatingModal}
              handleModalVisibility={handleModalVisibility}
              defaultPersonaId={(selectedPersona?.id || defaultPersonaId) ?? ""}
              isChatWithGroup={isGroupCharacter}
              typingMode={typingMode}
              cappedMessageId={cappedMessageId}
              isCutoffMessageLoaded={isCutoffMessageLoaded}
              onJumpToCutoff={handleJumpToCutoff}
            />
          </Col>
        </Row>
        <ChatDebugWidget conversationId={currentConversationId || undefined} />
        {/* The meters sit at the top of the chat on every device. */}
        <RulebookHud
          characterId={characterId}
          characterName={character?.name}
          conversationId={currentConversationId || undefined}
          conversation={conversation}
          altMsgs={altMsgs}
          altMsgPosition={altMsgPosition}
          isChatWithGroup={isGroupCharacter}
          isConversationLoaded={isRulebookHudConversationLoaded}
        />
        {/* messages section  */}
        <Col className="grow relative">
          <div
            className={"grow flex flex-col w-full left-0 items-center absolute h-full overflow-auto " + (isDesktop && "custom-scroll")}
            ref={chatContainerRef}
          >
            <Divider />

            {conversation.length <= 1 && (
              <div className="justify-end">
                <CapabilityGate capability={SubCapabilityKey.CharacterTranslate}>
                  <CharacterTranslation
                    dataTestId="CharacterTranslateCapabilityGate"
                    characterId={characterId}
                    characterLanguage={character?.language}
                    characterVisibility={character?.visibility}
                    style="chat"
                    onTranslationSuccess={handleTranslationSuccess}
                  />
                </CapabilityGate>
              </div>
            )}
            <div className="max-w-[620px]">
              <ChatHeroContainer chatHeroData={chatHeroData} disclaimer={disclaimer} chatHeroAlertProps={chatHeroAlertOptions} />
            </div>
            <Spacer y={2.5} />
            <div className={"flex-shrink-0 py-0 w-full"}>
              {lastMessageId || isLastMessageIdDeleted ? (
                <Row justify="center" className="pb-0">
                  {!isLoadingMessages && (
                    <SpicyButton variant="secondary_short" color="neutral" onPress={handleLoadPreviousMessages}>
                      <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}>
                        <Trans i18nKey="chat:page.action.loadPreviousMessages" />
                      </Text>
                    </SpicyButton>
                  )}
                  {isLoadingMessages ? <Spinner size="sm" /> : null}
                </Row>
              ) : null}
              {messagesToRender?.map((message: IConversationMessage, idx: number) => {
                const isAltMsg = altMsgs[altMsgPosition]?.id === message?.id;
                return (
                  <div key={message.id || idx} id={`message-${message.id}`}>
                    {!isPartner && conversation?.length > CAPPED_NOTICE_MIN_MESSAGE_LIMIT && cappedMessageId === message?.id && (
                      <CappedMessagesNotice />
                    )}
                    <Row>
                      <Col className="flex flex-col items-center">
                        <ChatBubble
                          {...uniqueBubbleProps}
                          {...commonBubbleBarProps}
                          chatIndex={idx}
                          character={chooseCharacterFromMessage({ message, character })}
                          createConversation={handleCreateConversation}
                          isSelectedForClone={shouldCurrentMessageBeSelectedForCloning(idx)}
                          isSelectedForRemoval={removeMessageIdx !== null && idx >= removeMessageIdx}
                          loadMessages={loadMessages}
                          message={message}
                          {...(isAltMsg && { altMsgs })}
                          {...(isAltMsg && { isTyping: typingMode })}
                          removalMode={removalMode}
                          requestErrorHandler={(idx === errorTargetBubbleIndex && !isGenericSendError && requestErrorHandler) || undefined}
                          setConversation={isAltMsg ? setAltMsgs : setConversation}
                          isChatWithGroup={isGroupCharacter}
                          handleEditPendingMessage={handleEditPendingMessage}
                          onUserUpdateMessage={onUserUpdateMessage}
                          isLoading={isLoading}
                          loadingType={loadingType}
                        >
                          {isAltMsg ? (
                            <ChatBar
                              {...uniqueChatBarProps}
                              {...commonBubbleBarProps}
                              onClickPrevious={handleClickPrevious}
                              onClickNext={handleClickNext}
                              isClonePartial={isUserSelectsPartialCloning}
                              removalMode={removalMode}
                              altMsgs={altMsgs}
                              character={character}
                              count={altMsgPosition + 1}
                              isChatWithGroup={isGroupCharacter}
                              isGroupBelowMinimum={isGroupBelowMinimum}
                            />
                          ) : (
                            <ChatBar
                              {...commonBubbleBarProps}
                              {...uniqueChatBarProps}
                              isChatWithGroup={isGroupCharacter}
                              hasPendingMessage={hasPendingMessage}
                              isLastMessage={idx === messagesToRender.length - 1}
                              isGroupBelowMinimum={isGroupBelowMinimum}
                            />
                          )}
                        </ChatBubble>
                      </Col>
                    </Row>
                  </div>
                );
              })}

              {typingMode && !errorMessage && typingMode !== TypingMode.AUTOPILOT ? (
                <Row className="justify-center">
                  <TypingBubble
                    typingMode={typingMode}
                    character={characterThatIsTyping}
                    user_name={name}
                    user_avatar_url={user_avatar_url}
                  ></TypingBubble>
                </Row>
              ) : null}
              {showGroupChat ? (
                <GroupChatCharacterSelector
                  autoResponse={autoResponse}
                  hasPendingMessage={hasPendingMessage}
                  isGroupChatAutoResponseAllowed={!!isGroupChatAutoResponseAllowed}
                  isGroupChatAutoResponsePaywall={isGroupChatAutoResponsePaywall}
                  handleCharacterClick={handleCharacterClick}
                  sub_characters={subCharactersWithFallback}
                  isDisabled={isInputActionDisabled}
                />
              ) : null}
              {!isBotAccessAllowed || isBotCreatorBlocked ? (
                <AlertBubble
                  error={!isBotAccessAllowed ? AlertBubbleVariant.INVALID_ACCESS : AlertBubbleVariant.BLOCKED_BOT_CREATOR}
                  invalidUrlError={invalidUrlError}
                  character={character}
                />
              ) : null}

              <Spacer y={0.5} />
              {/* This is for the gap between last msg and chat input */}
            </div>
            {!enableEditableChatExperience && (
              <Grid
                onClick={handleShowWaitingQueueModal}
                className="justify-center fixed bottom-0 left-0 right-0 z-[2] gap-6 bg-colorWaitingBanner text-center px-3 pt-2 pb-2" // Or any background color to prevent content from showing through
              >
                <Col>
                  <Row justify="center" className="flex-wrap gap-5">
                    <Grid>
                      <Text>
                        <Trans i18nKey="chat:page.waitingStatus.position" values={{ position }} />
                      </Text>
                    </Grid>
                    <Grid align="center" className="self-center object-cover mt-1 cursor-pointer">
                      <Image src="/Assets/minimize.png" className="rounded-none" height={IMAGE_HEIGHT} width={IMAGE_WIDTH} />
                    </Grid>
                  </Row>
                  <Row justify="center">
                    <Text>
                      {Number(time_left) >= SECONDS_PER_MINUTE && (
                        <Trans
                          i18nKey="chat:page.waitingStatus.time"
                          values={{
                            timeLeft: t("chat:page.waitingStatus.time.minutes", { count: Math.floor(Number(time_left) / SECONDS_PER_MINUTE) }),
                          }}
                        />
                      )}
                    </Text>
                  </Row>
                </Col>
              </Grid>
            )}
          </div>
        </Col>
        <Spacer y={3} />
        {/* chat footer  */}

        <Row>
          <Col
            className={cn(
              "items-center p-md",
              (removalMode || isUserSelectsPartialCloning) && "bg-gray-1 dark:bg-gray-3 border-0 border-solid border-t-1 border-gray-5",
              isGroupBelowMinimum && "opacity-50",
            )}
          >
            {/* eslint-disable-next-line no-nested-ternary -- three mutually exclusive footer states: removal, cloning, or chat input */}
            {removalMode ? (
              <Row className="max-mob:flex-wrap gap-sm items-center w-full justify-between" style={chatPageMaxWidthStyle}>
                <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="text-gray-11">
                  <Trans i18nKey="chat:page.removal.instruction" />
                </Text>

                <Row className="max-mob:w-full gap-sm">
                  <Button
                    variant={ButtonVariant.OUTLINED}
                    size={ButtonSize.LARGE}
                    onClick={onCancelClick}
                    disabled={isRemoveMessageLoading}
                    className="max-mob:w-full"
                  >
                    <Trans i18nKey="common:cancel" />
                  </Button>
                  <Button
                    variant={ButtonVariant.CONTAINED}
                    color={ButtonColorVariant.ERROR}
                    onClick={handleRemoveMesages}
                    size={ButtonSize.LARGE}
                    loading={isRemoveMessageLoading}
                    disabled={removeMessageIdx === null || removeMessageIdx === undefined}
                    className="max-mob:w-full"
                  >
                    <Trans i18nKey="common:remove" />
                  </Button>
                </Row>
              </Row>
            ) : isUserSelectsPartialCloning ? (
              <Row className="max-mob:flex-wrap gap-sm items-center w-full justify-between" style={chatPageMaxWidthStyle}>
                <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
                  <Trans i18nKey="chat:page.cloning.instruction" />
                </Text>

                <Row className="max-mob:w-full gap-sm">
                  <Button variant={ButtonVariant.OUTLINED} size={ButtonSize.LARGE} onClick={onCancelClick} className="max-mob:w-full">
                    <Trans i18nKey="common:cancel" />
                  </Button>
                  <Button
                    size={ButtonSize.LARGE}
                    onClick={onCloneButtonClick}
                    disabled={selectedMessageIndexForPartialCloning < 0}
                    className="max-mob:w-full"
                  >
                    <Trans i18nKey="common:clone" />
                  </Button>
                </Row>
              </Row>
            ) : (
              <div className="w-full flex flex-col items-center gap-sm">
                {!isChatInputHidden ? (
                  <>
                    {isGroupBelowMinimum ? (
                      <div className="w-full" style={chatPageMaxWidthStyle}>
                        <Alert
                          data-testid="ChatGroupUnavailableBanner"
                          visible={true}
                          title={t("chat:page.alert.groupUnavailable.title")}
                          description={t("chat:page.alert.groupUnavailable.description")}
                          variant={AlertVariant.ERROR}
                          isDismissable={false}
                        />
                      </div>
                    ) : null}
                    {isGenericSendError ? (
                      <div className="w-full" style={chatPageMaxWidthStyle}>
                        <Alert
                          data-testid="ChatSendErrorBanner"
                          visible={true}
                          variant={AlertVariant.ALERT}
                          title={t("chat:page.error.sendFailed")}
                          isDismissable={true}
                          actionHandler={handleDismissSendError}
                        />
                      </div>
                    ) : null}
                    <ChatInput
                      errorCode={errorCode as string}
                      conversationId={currentConversationId}
                      isLastMessageImage={isLastMessageImage}
                      generate={generateWithTracking}
                      typingMode={typingMode}
                      character={character}
                      isCloneLoading={isCloningConversationInProgress || isCloneUpdateTitleLoading}
                      wrapperRef={chatInputWrapperRef}
                      handlePreferredVoiceActions={handlePreferredVoiceActions}
                      setIsInputActionDisabled={setIsInputActionDisabled}
                      isInputActionDisabled={isInputActionDisabled || isGroupBelowMinimum}
                      isGroupBelowMinimum={isGroupBelowMinimum}
                      isMessageEditMode={isMessageEditMode}
                      setCharacterThatIsTyping={setCharacterThatIsTyping}
                      isAutoResponseActivated={autoResponse}
                      onPendingMessage={handlePendingMessage}
                      hasPendingMessage={hasPendingMessage}
                      scrollToBottom={scrollToBottom}
                      isCharacterAvatarBlurred={isCharacterAvatarBlurred}
                      isChatWithGroup={isGroupCharacter}
                      droppedFile={droppedFile}
                      shouldRenderImageUploader={shouldRenderImageUploader}
                      setDroppedFile={setDroppedFile}
                    />
                  </>
                ) : null}
              </div>
            )}
          </Col>
        </Row>

        {(() => {
          if (!isPartner) {
            return <WaitingQueueModal showWaitingQueueModal={showWaitingQueueModal} setShowWaitingQueueModal={setShowWaitingQueueModal} />;
          }
        })()}
        <ErrorModal setErrorModalVisible={setErrorModalVisible} errorModalMessage={errorModalMessage} errorModalVisible={errorModalVisible} />
        {shouldRenderImageUploader ? <ImageDropOverlay isVisible={isDragOver} isValidFileType={isValidFileType} /> : null}
      </div>
      <>
        {isCloningConversationInProgress ? <ScreenLoader /> : null}
        <FlagModal
          handleFlagMessage={handleFlagMessage}
          isOpen={isFlagModal}
          onClose={handleFlagModalClose}
          isLoading={isFlaggingMessage}
          progressStep={progressStep}
          setProgressStep={setProgressStep}
        />

        {isCloneConversationModal ? (
          <CloneConversationModal
            isOpen={isCloneConversationModal}
            onOpenChange={(open) => {
              if (!open) {
                closeCloneConversationModal();
              }
            }}
            onCancel={onUserCancelCloning}
            onSubmit={onUserConfirmCloneChoice}
            isLoading={isCloningConversationInProgress}
          />
        ) : null}

        {rateVoiceModal?.isVisible ? (
          <VoiceRatingModal
            messageId={rateVoiceModal.messageId}
            voiceId={rateVoiceModal.voiceId}
            showRateVoiceModal={rateVoiceModal.isVisible}
            onCloseRateModal={handleRateVoiceQualityModalClose}
            rateVoiceQuality={rateVoiceQuality}
            setInfoMessageForRatingVoice={setInfoMessageForRatingVoice}
            loadingFromPreferredVoice={loadingFromPreferredVoice}
          />
        ) : null}
        {showVoiceUpgradeModal ? (
          <ChatModalVoiceUpgrade
            open={showVoiceUpgradeModal}
            onPressUpgrade={handleOnPressVoiceUpgrade}
            onOpenChange={setShowVoiceUpgradeModal}
            voiceUpgradeVariant={voiceUpgradeVariant}
            characterAvatarUrl={character.avatar_url as string}
            isCharacterAvatarBlurred={isCharacterAvatarBlurred}
          />
        ) : null}

        <UpgradeToPremiumModal
          title={t("chat:modal.tryAIAutoSelect.upgradePlan.title")}
          paywallRoles={autoResponseCapability?.paywallRoles}
          isOpen={showAutoResponseUpgradeModal}
          onClose={handleCloseAutoResponseUpgradeModal}
        />

        {showChatbotRatingModal ? (
          <CharacterRating
            characterId={characterId}
            conversationLength={conversation?.length}
            onClose={handleCloseChatbotRatingModal}
            open={showChatbotRatingModal}
          />
        ) : null}
        <AgeVerificationWarningModal
          version="chat"
          forceIsRequired={isAgeVerificationMandatory(userProfile?.age_verification?.status)}
          isOpen={isAgeVerificationVisible}
          onClose={handleCloseAgeVerification}
        />
        {/* Floating Upgrade CTA - Shows when conversation is capped */}
        {!isPartner && hasContextCapUpsell ? (
          <FloatingUpgradeCta visible={Boolean(cappedMessageId)} conversationId={currentConversationId ?? ""} cappedMessageId={cappedMessageId} />
        ) : null}
      </>
    </>
  );
};
